/* The record page (13-25), on the shell grid with a serif page head (D-13-15-2). Purple is identity only (the kicker
 * ref, the title, the area heads); chips are quiet; "Client id to add" is a question to answer, so it reads amber. */

/* 13.2-10: Oversight's possible-duplicate note (a signal only, never an action; amber-soft, never red). */
.dupNote {
  margin: 12px 0 0;
  padding: 10px 14px;
  font-size: 14px;
  line-height: 1.5;
  color: var(--color-text);
  background: var(--amber-soft);
  border: 1px solid var(--amber-line);
  border-radius: var(--radius-md);
}

.dupLine {
  margin: 0;
}

.dupTitle {
  color: var(--color-text-secondary);
}

.dupHint {
  margin: 4px 0 0;
  font-size: 12.5px;
  color: var(--color-text-secondary);
}

.head {
  padding-top: 44px;
  margin-bottom: 26px;
}

.kicker {
  margin: 0 0 6px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
}

.ref {
  color: var(--color-primary-dark);
  font-variant-numeric: tabular-nums;
}

.practiceTag {
  padding: 2px 10px;
  font-size: 11px;
  letter-spacing: 0.04em;
  color: var(--amber-dark);
  background: var(--amber-soft);
  border: 1px solid var(--amber-line);
  border-radius: var(--radius-full);
}

.title {
  margin: 0;
  padding-top: 0;
  font-family: var(--serif);
  font-weight: 400;
  font-size: clamp(28px, 3.6vw, 40px);
  line-height: 1.12;
  letter-spacing: -0.01em;
  color: var(--color-primary-dark);
  overflow-wrap: anywhere;
}

.chips {
  margin-top: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.chip,
.chipLink {
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  padding: 2px 11px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--color-text-secondary);
  background: var(--color-bg-card);
  border: 1px solid var(--line);
  border-radius: var(--radius-full);
}

.chipLink {
  color: var(--color-primary-dark);
  text-decoration: none;
}

.chipLink:hover {
  border-color: var(--sage-dark);
  background: var(--sage-light);
}

.chipLink:focus-visible {
  outline: 2px solid var(--color-border-focus);
  outline-offset: 2px;
}

.chipMine {
  color: var(--teal-dark);
  background: var(--teal-soft);
  border-color: var(--teal-soft);
}

.chipTodo {
  color: var(--amber-dark);
  background: var(--amber-soft);
  border-color: var(--amber-line);
}

.refreshing {
  font-size: 12.5px;
  color: var(--color-text-muted);
}

.savedNote {
  margin: 14px 0 0;
  padding: 10px 14px;
  font-size: 14px;
  color: var(--teal-dark);
  background: var(--teal-soft);
  border-radius: var(--radius-md);
}

.owed {
  margin: 12px 0 0;
  padding: 10px 14px;
  font-size: 14px;
  line-height: 1.5;
  color: var(--amber-dark);
  background: var(--amber-soft);
  border: 1px solid var(--amber-line);
  border-radius: var(--radius-md);
}

.textButton {
  padding: 0;
  font: inherit;
  font-weight: 600;
  color: inherit;
  text-decoration: underline;
  text-underline-offset: 2px;
  background: none;
  border: 0;
  cursor: pointer;
}

.textButton:focus-visible {
  outline: 2px solid var(--color-border-focus);
  outline-offset: 2px;
  border-radius: 4px;
}

/* ── Two columns on a wide screen: read on the left, act and the trails on the right ── */
.layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  grid-template-rows: auto auto 1fr;
  grid-template-areas:
    'read act'
    'read trail'
    'read closure';
  gap: 28px 32px;
  align-items: start;
}

.act {
  grid-area: act;
  min-width: 0;
}

.read {
  grid-area: read;
  min-width: 0;
}

.trail {
  grid-area: trail;
  min-width: 0;
}

.closure {
  grid-area: closure;
  min-width: 0;
}

.areaTitle {
  margin: 0 0 12px;
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
}

/* 13.2-11: an area's heading, with its "Expand all" / "Collapse all" text buttons beside it. */
.areaHead {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 6px 14px;
  flex-wrap: wrap;
  margin: 0 0 12px;
}

.areaHead .areaTitle {
  margin: 0;
}

.allToggle {
  display: flex;
  gap: 14px;
}

.allButton {
  padding: 0;
  font: inherit;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--color-primary-dark);
  text-decoration: underline;
  text-underline-offset: 2px;
  background: none;
  border: 0;
  cursor: pointer;
}

.allButton:disabled {
  color: var(--color-text-muted);
  text-decoration: none;
  cursor: default;
}

.allButton:focus-visible {
  outline: 2px solid var(--color-border-focus);
  outline-offset: 2px;
  border-radius: 4px;
}

/* A `#panel-<key>` link lands below the sticky app bar, not under it. */
.layout [id^='panel-'],
.layout [id^='fv-'] {
  scroll-margin-top: 96px;
}

.muted {
  margin: 0;
  font-size: 13.5px;
  line-height: 1.5;
  color: var(--color-text-secondary);
}

.lede {
  margin: 14px 0 0;
  max-width: 66ch;
  font-size: 15px;
  line-height: 1.55;
  color: var(--color-text);
}

.lede p {
  margin: 0;
}

.loading {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 60px 0;
  color: var(--color-text-secondary);
}

.failure {
  margin: 16px 0;
  padding: 10px 14px;
  font-size: 14px;
  color: var(--red-dark);
  background: var(--red-soft);
  border-radius: var(--radius-md);
}

.links {
  margin: 10px 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 13.5px;
  line-height: 1.5;
}

.link {
  overflow-wrap: anywhere;
}

.linkRel {
  color: var(--color-text-secondary);
}

.linkTitle {
  color: var(--color-text);
}

/* The page stacks: act first, then the record, then the trails. */
@media (max-width: 960px) {
  .layout {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: none;
    grid-template-areas:
      'act'
      'read'
      'trail'
      'closure';
  }
}

@media (max-width: 720px) {
  .head {
    padding-top: 28px;
  }
}
